import React, { useState } from "react";
import IdentityInventoryPanel from "./IdentityInventoryPanel";
import DeviceInventoryPanel from "./DeviceInventoryPanel";
import IdentityCoverageCard from "./IdentityCoverageCard";
import IdentityMap from "./IdentityMap";

/**
 * GRC → Assets: the objects the platform holds and computes controls from — identities first;
 * devices, AI assets, cloud apps and cloud resources attach to the same tab as their feeds land
 * (one tab, a type switcher, never a page per feed). Assets are never controls: nothing here
 * scores, and the Controls tab shows the assurance controls computed from them.
 */
export const ASSET_TYPES = [
  { key: "identity", label: "Identities", available: true },
  { key: "device",   label: "Devices",    available: true },
  // The same objects as the two lists beside it, drawn as a chain instead of read as rows. It
  // belongs here rather than on its own page: a map that lived elsewhere would be a second view of
  // one estate that could drift from the inventory it is drawn from.
  { key: "map",      label: "Map",        available: true },
];

export default function AssetsTab({ organizationId, initialType = "identity", initialIdentityId = null, initialDeviceId = null, initialSearch = null, onOpenControls }) {
  const [type, setType] = useState(ASSET_TYPES.some(t => t.key === initialType && t.available) ? initialType : "identity");

  return (
    <div className="max-w-7xl mx-auto px-5 pb-8 pt-4 space-y-4" data-testid="assets-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1" role="tablist" aria-label="Asset type">
          {ASSET_TYPES.map(t => (
            <button key={t.key} type="button" role="tab" aria-selected={type === t.key} disabled={!t.available}
              title={t.available ? undefined : t.note}
              onClick={() => t.available && setType(t.key)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${type === t.key ? "bg-indigo-600 text-white" : t.available ? "text-gray-700 hover:bg-gray-100" : "text-gray-300 cursor-not-allowed"}`}>
              {t.label}{!t.available && <span className="ml-1 text-[10px] uppercase tracking-wide">soon</span>}
            </button>
          ))}
        </div>
        <div className="text-xs text-gray-500">
          Objects the controls are computed from. Assets never score — the readiness effect lives in{" "}
          {onOpenControls
            ? <button type="button" onClick={onOpenControls} className="text-indigo-600 hover:underline font-medium">Controls → Identity assurance</button>
            : <span className="font-medium">Controls → Identity assurance</span>}.
        </div>
      </div>

      {type === "identity" && <IdentityCoverageCard organizationId={organizationId} />}
      {type === "identity" && <IdentityInventoryPanel organizationId={organizationId} initialIdentityId={initialIdentityId} initialSearch={initialSearch} />}
      {type === "device" && <DeviceInventoryPanel organizationId={organizationId} initialDeviceId={initialDeviceId} />}
      {type === "map" && (
        <>
          <p className="text-xs text-gray-500">
            Identity → privilege → device → application → data reach → business service. A dot&apos;s size
            is the exposure it carries; a dashed grey dot is something Axaris could not read, with the
            reason on the right. Click a dot to isolate just that object&apos;s chain; drag any dot.
          </p>
          <IdentityMap organizationId={organizationId} height={620} />
        </>
      )}
    </div>
  );
}
